import { io } from "socket.io-client";

/**
 * Socket.IO client for Captify realtime updates.
 * Auth matches REST: JWT from localStorage `authToken`.
 */
let socket = null;

export function getSocketUrl() {
  const base = (import.meta.env.VITE_BASE_URL || "").replace(/\/$/, "");
  if (!base) return "";
  try {
    // Same API host as REST; Socket.IO mounts at /socket.io on the host (not under /api).
    return new URL(base).origin;
  } catch {
    return base;
  }
}

export function getAccessToken() {
  return localStorage.getItem("authToken");
}

export function getSocket() {
  return socket;
}

export function connectSocket() {
  const token = getAccessToken();
  const url = getSocketUrl();

  if (!token || !url) {
    if (!token) {
      console.warn("[socket] skip connect — missing authToken");
    } else {
      console.warn("[socket] skip connect — missing VITE_BASE_URL");
    }
    return null;
  }

  if (socket?.connected) {
    return socket;
  }

  if (socket) {
    socket.auth = { token };
    socket.connect();
    return socket;
  }

  socket = io(url, {
    auth: { token },
    withCredentials: true,
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    path: "/socket.io",
    transports: ["websocket", "polling"],
  });

  socket.on("connect", () => {
    console.log("[socket] connected", socket.id, "→", url);
  });

  socket.on("disconnect", (reason) => {
    console.warn("[socket] disconnected:", reason);
  });

  socket.on("connect_error", (err) => {
    console.warn("[socket] connect_error:", err?.message || err);
  });

  // Refresh JWT on every reconnect attempt.
  socket.io.on("reconnect_attempt", () => {
    const nextToken = getAccessToken();
    if (nextToken) {
      socket.auth = { token: nextToken };
    }
  });

  return socket;
}

export function disconnectSocket() {
  if (!socket) return;
  socket.removeAllListeners();
  socket.io?.removeAllListeners?.();
  socket.disconnect();
  socket = null;
}

export function onSocketEvent(event, handler) {
  const active = connectSocket();
  if (!active) return () => {};

  active.on(event, handler);
  return () => {
    active.off(event, handler);
  };
}
